import React, { Suspense, useEffect, useLayoutEffect, useRef } from "react";
import "@/App.css";
import {
  BrowserRouter,
  Navigate,
  Routes,
  Route,
  useLocation,
  useNavigate
} from "react-router-dom";
import Header from "./components/Header";
import AnnouncementBanner from "./components/AnnouncementBanner";
import AccessEndingBanner from "./components/AccessEndingBanner";
import BillingNoticeBanner from "./components/BillingNoticeBanner";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminPinGate from "./components/AdminPinGate";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
// Page stylesheets stay global even though the pages below load on demand:
// every public page depends on base rules only some of them import (e.g.
// .public-button/.public-kicker live solely in PublicHome.css). Listed in
// the order the pages used to pull them in, so the cascade is unchanged.
import "./pages/PublicHome.css";
import "./components/OnScreenKeypad.css";
import "./components/AuthShell.css";
import "./pages/PublicSeo.css";
import "./pages/PublicMockTestPage.css";
import "./pages/CompassGuidePage.css";
import "./pages/AonGuidePage.css";
import "./pages/IosAppPage.css";
// Loaded eagerly, unlike every other page below: these are prerendered with
// their JavaScript kept (staticHtml: false), and the app mounts with
// createRoot rather than hydrating -- a lazy page would swap that
// prerendered content for the loading fallback until its chunk arrived.
import ContactDeveloper from "./pages/ContactDeveloper";
import DonatePage from "./pages/DonatePage";
import PreparationPlanPage from "./pages/PreparationPlanPage";
import SeoMeta from "./components/SeoMeta";
import PublicHeader from "./components/PublicHeader";
import TrackSelectionModal from "./components/TrackSelectionModal";
import { CFASTButton } from "./modules/cbtCommon";
import { IOS_APP_PAGE_LIVE } from "./data/iosApp";
import { apiBaseUrl } from "./lib/api";
import SiteDisclaimer from "./components/SiteDisclaimer";
import TermsAcceptanceGate from "./components/TermsAcceptanceGate";
import { trackGrowth } from "./utils/growthAnalytics";
import { lazyNamedWithReload, lazyWithReload } from "./utils/lazyWithReload";

// Every other page loads on demand, so a visitor starting a free practice
// test from an ad no longer downloads the dashboards, admin, commercial
// guides and their content up front.
const PublicHome = lazyWithReload(() => import("./pages/PublicHome"));
const Dashboard = lazyWithReload(() => import("./pages/Dashboard"));
const CommercialDashboard = lazyWithReload(() => import("./pages/CommercialDashboard"));
const CommercialGuidesPage = lazyWithReload(() => import("./pages/CommercialGuidesPage"));
const OnboardingPage = lazyWithReload(() => import("./pages/OnboardingPage"));
const AdminSettings = lazyWithReload(() => import("./pages/AdminSettings"));
const ModuleRouter = lazyWithReload(() => import("./pages/ModuleRouter"));
const SettingsPage = lazyWithReload(() => import("./pages/SettingsPage"));
const SubscriptionPage = lazyWithReload(() => import("./pages/SubscriptionPage"));
const ScoreHistory = lazyWithReload(() => import("./pages/ScoreHistory"));
const ScoreView = lazyWithReload(() => import("./pages/ScoreView"));
const SubscriptionSuccess = lazyWithReload(() => import("./pages/SubscriptionSuccess"));
const SubscriptionCancel = lazyWithReload(() => import("./pages/SubscriptionCancel"));
const AuthPage = lazyWithReload(() => import("./pages/AuthPage"));
const AuthVerify = lazyWithReload(() => import("./pages/AuthVerify"));
const ResetPassword = lazyWithReload(() => import("./pages/ResetPassword"));
const TermsPage = lazyWithReload(() => import("./pages/TermsPage"));
const MethodologyPage = lazyWithReload(() => import("./pages/MethodologyPage"));
const PublicPracticePage = lazyWithReload(() => import("./pages/PublicPracticePage"));
const PublicMockTestPage = lazyWithReload(() => import("./pages/PublicMockTestPage"));
const ServiceLandingPage = lazyWithReload(() => import("./pages/ServiceLandingPage"));
const CbatGuidePage = lazyWithReload(() => import("./pages/CbatGuidePage"));
const PricingPage = lazyWithReload(() => import("./pages/PricingPage"));
const RafPilotPage = lazyWithReload(() => import("./pages/RafPilotPage"));
const RafStaninesPage = lazyWithReload(() => import("./pages/RafStaninesPage"));
const CfastGuidePage = lazyWithReload(() => import("./pages/CfastGuidePage"));
const AdfGuidePage = lazyWithReload(() => import("./pages/AdfGuidePage"));
const PublicModulePage = lazyWithReload(() => import("./pages/PublicModulePage"));
const PublicModulePracticePage = lazyWithReload(() => import("./pages/PublicModulePracticePage"));
const GuestPracticePage = lazyWithReload(() => import("./pages/GuestPracticePage"));
const AboutPage = lazyWithReload(() => import("./pages/AboutPage"));
const PrivacyPage = lazyWithReload(() => import("./pages/PrivacyPage"));
const GuidesPage = lazyWithReload(() => import("./pages/GuidesPage"));
const SourcesPage = lazyWithReload(() => import("./pages/SourcesPage"));
const RafTwoWeekSchedulePage = lazyWithReload(() => import("./pages/RafTwoWeekSchedulePage"));
const CbatVsDaaPage = lazyWithReload(() => import("./pages/CbatVsDaaPage"));
const CommercialLandingPage = lazyWithReload(() => import("./pages/CommercialLandingPage"));
const CommercialGuidePage = lazyWithReload(() => import("./pages/CommercialGuidePage"));
const CommercialPricingPage = lazyWithReload(() => import("./pages/CommercialPricingPage"));
const CommercialProviderPage = lazyWithReload(() => import("./pages/CommercialProviderPage"));
const AdaptCognitiveReasoningPage = lazyWithReload(() => import("./pages/adapt/AdaptCognitiveReasoningPage"));
const AdaptMathsPage = lazyWithReload(() => import("./pages/adapt/AdaptMathsPage"));
const AdaptPhysicsPage = lazyWithReload(() => import("./pages/adapt/AdaptPhysicsPage"));
const AdaptFastPage = lazyWithReload(() => import("./pages/adapt/AdaptFastPage"));
const AdaptControlCoordinationPage = lazyWithReload(() => import("./pages/adapt/AdaptControlCoordinationPage"));
const AdaptPersonalityPage = lazyWithReload(() => import("./pages/adapt/AdaptPersonalityPage"));
const CompassGuidePage = lazyWithReload(() => import("./pages/CompassGuidePage"));
const AonGuidePage = lazyWithReload(() => import("./pages/AonGuidePage"));
const IosAppPage = lazyWithReload(() => import("./pages/IosAppPage"));
const NotFoundPage = lazyWithReload(() => import("./pages/NotFoundPage"));
const MockTestHistoryPage = lazyWithReload(() => import("./pages/MockTestHistoryPage"));
const importMockTestPage = () => import("./pages/MockTestPage");
const MockTestBreakPage = lazyNamedWithReload(importMockTestPage, "MockTestBreakPage");
const MockTestLobby = lazyNamedWithReload(importMockTestPage, "MockTestLobby");
const MockTestResultsPage = lazyNamedWithReload(importMockTestPage, "MockTestResultsPage");
const MockTestRunPage = lazyNamedWithReload(importMockTestPage, "MockTestRunPage");
const importCommercialDiagnosticPage = () => import("./pages/CommercialDiagnosticPage");
const CommercialDiagnosticIntro = lazyNamedWithReload(importCommercialDiagnosticPage, "CommercialDiagnosticIntro");
const CommercialDiagnosticRunPage = lazyNamedWithReload(importCommercialDiagnosticPage, "CommercialDiagnosticRunPage");
const CommercialDiagnosticBreakPage = lazyNamedWithReload(importCommercialDiagnosticPage, "CommercialDiagnosticBreakPage");
const CommercialDiagnosticResultsPage = lazyNamedWithReload(importCommercialDiagnosticPage, "CommercialDiagnosticResultsPage");
const importCommercialFullBatteryPage = () => import("./pages/CommercialFullBatteryPage");
const CommercialFullBatteryIntro = lazyNamedWithReload(importCommercialFullBatteryPage, "CommercialFullBatteryIntro");
const CommercialFullBatteryRunPage = lazyNamedWithReload(importCommercialFullBatteryPage, "CommercialFullBatteryRunPage");
const CommercialFullBatteryBreakPage = lazyNamedWithReload(importCommercialFullBatteryPage, "CommercialFullBatteryBreakPage");
const CommercialFullBatteryResultsPage = lazyNamedWithReload(importCommercialFullBatteryPage, "CommercialFullBatteryResultsPage");

window.cbatGrowthApiBase = apiBaseUrl;

// Deliberately not a <main> with real text: prerendering waits for <main>
// to hold 250+ characters before snapshotting, and this must never be what
// gets captured. The spinner fades in after a beat, so a chunk that arrives
// quickly never flashes one at all.
const RouteLoading = () => (
  <div className="site-page flex min-h-screen items-center justify-center" role="status" aria-label="Loading">
    <span className="route-loading h-4 w-4 rounded-full border-2 border-[#263b53] border-t-[#68d9ff]" />
  </div>
);


const PrivatePage = ({ children }) => (
  <ProtectedRoute>{children}</ProtectedRoute>
);

// Commercial Pilot Prep is still being built out -- hidden from regular
// users (admins can still see/test it) until the site-wide toggle is on.
const useCommercialTrackVisible = () => {
  const { isAdmin, siteSettings } = useAuth();
  return isAdmin || siteSettings?.commercial_track_enabled === true;
};

const ActiveDashboard = () => {
  const { profile } = useAuth();
  const commercialTrackVisible = useCommercialTrackVisible();
  return (
    <>
      {commercialTrackVisible && profile?.preparation_preferences?.active_family === 'commercial'
        ? <CommercialDashboard />
        : <Dashboard />}
      <TrackSelectionModal />
    </>
  );
};

const CommercialDashboardGate = () => {
  const commercialTrackVisible = useCommercialTrackVisible();
  return commercialTrackVisible
    ? <><CommercialDashboard /><TrackSelectionModal /></>
    : <Navigate to="/dashboard" replace />;
};

// Strategy guides are informational (not gated on hasFullAccess like the
// diagnostic/full battery) -- they help justify a subscription, so a
// non-subscribed commercial user can still read them.
const CommercialGuidesGate = () => {
  const commercialTrackVisible = useCommercialTrackVisible();
  return commercialTrackVisible ? <CommercialGuidesPage /> : <Navigate to="/dashboard" replace />;
};

const CommercialSubscriptionRequired = () => {
  const navigate = useNavigate();
  return (
    <div className="site-page flex min-h-screen items-center justify-center text-white">
      <div className="site-panel w-full max-w-[760px] overflow-hidden">
        <div className="site-panel__header">
          <span>Subscription required</span>
          <span className="text-[#f4c86d]">● Restricted</span>
        </div>
        <div className="space-y-5 p-6 text-sm leading-relaxed text-[#b9cbdd]">
          <p>
            The Commercial Pilot Aptitude Diagnostic is part of the Commercial Pilot Prep subscription.
          </p>
          <div className="flex justify-end gap-2">
            <CFASTButton onClick={() => navigate('/subscription')}>View Plans</CFASTButton>
            <CFASTButton className="site-button--primary" onClick={() => navigate('/commercial/dashboard')}>Back to Dashboard</CFASTButton>
          </div>
        </div>
      </div>
    </div>
  );
};

// Generic gate for any commercial feature that requires an active
// subscription (not just the diagnostic) -- the diagnostic, and now the
// full battery, both use it.
const commercialPaidFeatureGate = (Page) => () => {
  const commercialTrackVisible = useCommercialTrackVisible();
  const { hasFullAccess, isAdmin } = useAuth();
  if (!commercialTrackVisible) return <Navigate to="/dashboard" replace />;
  if (!hasFullAccess && !isAdmin) return <CommercialSubscriptionRequired />;
  return <Page />;
};
const CommercialDiagnosticIntroGate = commercialPaidFeatureGate(CommercialDiagnosticIntro);
const CommercialDiagnosticRunGate = commercialPaidFeatureGate(CommercialDiagnosticRunPage);
const CommercialDiagnosticBreakGate = commercialPaidFeatureGate(CommercialDiagnosticBreakPage);
const CommercialDiagnosticResultsGate = commercialPaidFeatureGate(CommercialDiagnosticResultsPage);
const CommercialFullBatteryIntroGate = commercialPaidFeatureGate(CommercialFullBatteryIntro);
const CommercialFullBatteryRunGate = commercialPaidFeatureGate(CommercialFullBatteryRunPage);
const CommercialFullBatteryBreakGate = commercialPaidFeatureGate(CommercialFullBatteryBreakPage);
const CommercialFullBatteryResultsGate = commercialPaidFeatureGate(CommercialFullBatteryResultsPage);

const HomePage = () => {
  const { user, loading, profile, isConfigured } = useAuth();
  const profileKnown = !isConfigured || (profile && Object.prototype.hasOwnProperty.call(profile, 'service_profile'));
  if (loading || (user && !profileKnown)) {
    return (
      <div className="site-page flex min-h-screen items-center justify-center text-white">
        <div className="site-panel flex w-full max-w-md items-center gap-3 p-5 text-sm text-[#b9cbdd]">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#263b53] border-t-[#68d9ff]" />
          Initialising CBAT Academy…
        </div>
      </div>
    );
  }
  if (!user) return <PublicHome />;
  if (isConfigured && !profile?.service_profile && !profile?.preparation_preferences?.commercial_enabled) {
    return <Navigate to="/onboarding" replace />;
  }
  return <Navigate to="/dashboard" replace />;
};

const pageTitles = [
  { match: (path) => path === '/auth', title: 'Login | CBAT Academy' },
  { match: (path) => path === '/auth/verify', title: 'Verify Account | CBAT Academy' },
  { match: (path) => path === '/auth/reset-password', title: 'Reset Password | CBAT Academy' },
  { match: (path) => path === '/terms', title: 'Terms of Use | CBAT Academy' },
  { match: (path) => path === '/privacy', title: 'Privacy | CBAT Academy' },
  { match: (path) => path === '/about', title: 'About | CBAT Academy' },
  { match: (path) => path === '/methodology', title: 'Methodology | CBAT Academy' },
  { match: (path) => path === '/contact', title: 'Contact | CBAT Academy' },
  { match: (path) => path === '/donate', title: 'Support the Project | CBAT Academy' },
  { match: (path) => path === '/dashboard', title: 'Dashboard | CBAT Academy' },
  { match: (path) => path === '/commercial/dashboard', title: 'Commercial Pilot Prep Dashboard | CBAT Academy' },
  { match: (path) => path.startsWith('/commercial/diagnostic'), title: 'Commercial Pilot Aptitude Diagnostic | CBAT Academy' },
  { match: (path) => path.startsWith('/commercial/full-battery'), title: 'Commercial Full Battery | CBAT Academy' },
  { match: (path) => path === '/commercial/guides', title: 'Strategy Guides | CBAT Academy' },
  { match: (path) => path === '/onboarding', title: 'Set Up Training Profile | CBAT Academy' },
  { match: (path) => path === '/admin', title: 'Admin | CBAT Academy' },
  { match: (path) => path === '/settings', title: 'Settings | CBAT Academy' },
  { match: (path) => path.startsWith('/module/'), title: 'Training | CBAT Academy' },
  { match: (path) => path === '/mock-test/history', title: 'Full Battery History | CBAT Academy' },
  { match: (path) => path.startsWith('/mock-test'), title: 'Mock Test | CBAT Academy' },
  { match: (path) => path === '/scores', title: 'Scores | CBAT Academy' },
  { match: (path) => path.startsWith('/history'), title: 'History | CBAT Academy' },
  { match: (path) => path.startsWith('/subscription'), title: 'Subscription | CBAT Academy' }
];

const PageTitle = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === '/'
      || location.pathname.startsWith('/raf-cbat')
      || location.pathname.startsWith('/cfast')
      || location.pathname === '/adf-guide'
      || location.pathname.startsWith('/commercial-')
      || ['/about', '/privacy', '/methodology', '/terms', '/guides', '/sources', '/contact', '/donate', '/raf-two-week-schedule', '/cbat-vs-daa', '/preparation-plan'].includes(location.pathname)
      || location.pathname.startsWith('/auth')) return;
    const entry = pageTitles.find((item) => item.match(location.pathname));
    document.title = entry?.title || 'CBAT Academy';
  }, [location.pathname]);

  return null;
};

const PrivateMetadata = () => {
  const { pathname } = useLocation();
  const isPrivate = pathname === '/auth'
    || pathname.startsWith('/auth/')
    || ['/dashboard', '/commercial/dashboard', '/commercial/diagnostic', '/commercial/full-battery', '/commercial/guides', '/onboarding', '/admin', '/settings', '/subscription', '/gamepad', '/scores', '/history', '/account'].some((path) => pathname === path || pathname.startsWith(`${path}/`))
    || pathname.startsWith('/module/')
    || pathname.startsWith('/mock-test')
    || pathname.startsWith('/subscription/');

  return isPrivate ? (
    <SeoMeta title="CBAT Academy" description="CBAT Academy account and training area." path={pathname} robots="noindex,follow" image={null} />
  ) : null;
};


const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useLayoutEffect(() => {
    if (hash) {
      const frame = window.requestAnimationFrame(() => {
        document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' });
      });
      return () => window.cancelAnimationFrame(frame);
    }

    window.scrollTo(0, 0);
    const frame = window.requestAnimationFrame(() => window.scrollTo(0, 0));

    return () => window.cancelAnimationFrame(frame);
  }, [hash, pathname]);

  return null;
};

function AppRoutes() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  useEffect(() => {
    trackGrowth('page_view', location.pathname);
    window.cbatGrowth?.updateOptOut();
  }, [location.pathname]);
  const previousPathRef = useRef(location.pathname);
  const showHeader = !loading && user && !location.pathname.startsWith('/auth');
  const showPublicHeader = !loading && !user;

  useEffect(() => {
    const previousPath = previousPathRef.current;
    const practiceMatch = previousPath.match(/^\/raf-cbat\/([^/]+)\/practice$/);

    if (location.pathname === '/' && !user && practiceMatch) {
      navigate(`/raf-cbat/${practiceMatch[1]}`, { replace: true });
      return;
    }

    previousPathRef.current = location.pathname;
  }, [location.pathname, navigate, user]);

  return (
    <>
      <ScrollToTop />
      <PageTitle />
      <PrivateMetadata />
      {showHeader && <Header />}
      {showHeader && <AnnouncementBanner />}
      {showHeader && <AccessEndingBanner />}
      {showHeader && <BillingNoticeBanner />}
      {showPublicHeader && <PublicHeader />}
      <Suspense fallback={<RouteLoading />}>
      <Routes>
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/auth/verify" element={<AuthVerify />} />
        <Route path="/auth/reset-password" element={<ResetPassword />} />
        <Route path="/legal" element={<Navigate to="/terms" replace />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/ios-app" element={IOS_APP_PAGE_LIVE ? <IosAppPage /> : <Navigate to="/" replace />} />
        <Route path="/guides" element={<GuidesPage />} />
        <Route path="/methodology" element={<MethodologyPage />} />
        <Route path="/sources" element={<SourcesPage />} />
        <Route path="/donate" element={<DonatePage />} />
        <Route path="/raf-cbat" element={<ServiceLandingPage site="raf" />} />
        <Route path="/raf-cbat-practice" element={<PublicPracticePage />} />
        <Route path="/raf-cbat-mock-test" element={<PublicMockTestPage />} />
        <Route path="/raf-cbat-guide" element={<CbatGuidePage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/raf-pricing" element={<Navigate to="/pricing" replace />} />
        <Route path="/raf-cbat-quick-test" element={<GuestPracticePage />} />
        <Route path="/raf-two-week-schedule" element={<RafTwoWeekSchedulePage />} />
        <Route path="/preparation-plan" element={<PreparationPlanPage family="military" />} />
        <Route path="/cbat-vs-daa" element={<CbatVsDaaPage />} />
        <Route path="/raf-cbat/pilot" element={<RafPilotPage />} />
        <Route path="/raf-cbat/stanines" element={<RafStaninesPage />} />
        <Route path="/raf-cbat/:moduleId/practice" element={<PublicModulePracticePage />} />
        <Route path="/raf-cbat/:moduleId" element={<PublicModulePage />} />
        <Route path="/cfast" element={<ServiceLandingPage site="cfast" />} />
        <Route path="/cfast-practice" element={<PublicPracticePage site="cfast" />} />
        <Route path="/cfast-guide" element={<CfastGuidePage />} />
        <Route path="/cfast/:moduleId/practice" element={<PublicModulePracticePage site="cfast" />} />
        <Route path="/cfast/:moduleId" element={<PublicModulePage site="cfast" />} />
        <Route path="/adf" element={<ServiceLandingPage site="adf" />} />
        <Route path="/adf-guide" element={<AdfGuidePage />} />
        <Route path="/commercial" element={<CommercialLandingPage />} />
        <Route path="/commercial-guide" element={<CommercialGuidePage />} />
        <Route path="/commercial-preparation-plan" element={<PreparationPlanPage family="commercial" />} />
        <Route path="/commercial-pricing" element={<CommercialPricingPage />} />
        <Route path="/commercial-adapt" element={<CommercialProviderPage provider="adapt" />} />
        <Route path="/commercial-adapt/cognitive-reasoning" element={<AdaptCognitiveReasoningPage />} />
        <Route path="/commercial-adapt/maths" element={<AdaptMathsPage />} />
        <Route path="/commercial-adapt/physics" element={<AdaptPhysicsPage />} />
        <Route path="/commercial-adapt/fast" element={<AdaptFastPage />} />
        <Route path="/commercial-adapt/control-coordination" element={<AdaptControlCoordinationPage />} />
        <Route path="/commercial-adapt/personality" element={<AdaptPersonalityPage />} />
        <Route path="/commercial-compass" element={<Navigate to="/commercial/compass" replace />} />
        <Route path="/commercial/compass" element={<CompassGuidePage />} />
        <Route path="/commercial/compass/:guideId" element={<CompassGuidePage />} />
        <Route path="/commercial-aon-cut-e" element={<Navigate to="/commercial/aon-cut-e" replace />} />
        <Route path="/commercial/aon-cut-e" element={<AonGuidePage />} />
        <Route path="/commercial/aon-cut-e/:guideId" element={<AonGuidePage />} />
        <Route path="/commercial-pilapt" element={<CommercialProviderPage provider="pilapt" />} />
        <Route path="/commercial-practice/:moduleId/practice" element={<PublicModulePracticePage site="commercial" />} />
        <Route path="/commercial-practice/:moduleId" element={<PublicModulePage site="commercial" />} />
        <Route path="/contact" element={<ContactDeveloper />} />
        <Route path="/" element={<HomePage />} />
        <Route path="/dashboard" element={<PrivatePage><ActiveDashboard /></PrivatePage>} />
        <Route path="/commercial/dashboard" element={<PrivatePage><CommercialDashboardGate /></PrivatePage>} />
        <Route path="/commercial/guides" element={<PrivatePage><CommercialGuidesGate /></PrivatePage>} />
        <Route path="/commercial/diagnostic" element={<PrivatePage><CommercialDiagnosticIntroGate /></PrivatePage>} />
        <Route path="/commercial/diagnostic/run/:moduleId" element={<PrivatePage><CommercialDiagnosticRunGate /></PrivatePage>} />
        <Route path="/commercial/diagnostic/break" element={<PrivatePage><CommercialDiagnosticBreakGate /></PrivatePage>} />
        <Route path="/commercial/diagnostic/results" element={<PrivatePage><CommercialDiagnosticResultsGate /></PrivatePage>} />
        <Route path="/commercial/full-battery" element={<PrivatePage><CommercialFullBatteryIntroGate /></PrivatePage>} />
        <Route path="/commercial/full-battery/run/:moduleId" element={<PrivatePage><CommercialFullBatteryRunGate /></PrivatePage>} />
        <Route path="/commercial/full-battery/break" element={<PrivatePage><CommercialFullBatteryBreakGate /></PrivatePage>} />
        <Route path="/commercial/full-battery/results" element={<PrivatePage><CommercialFullBatteryResultsGate /></PrivatePage>} />
        <Route path="/onboarding" element={<PrivatePage><OnboardingPage /></PrivatePage>} />
        <Route path="/admin" element={<PrivatePage><AdminPinGate><AdminSettings /></AdminPinGate></PrivatePage>} />
        <Route path="/settings" element={<PrivatePage><SettingsPage /></PrivatePage>} />
        <Route path="/subscription" element={<PrivatePage><SubscriptionPage /></PrivatePage>} />
        <Route path="/module/:moduleId" element={<PrivatePage><ModuleRouter /></PrivatePage>} />
        <Route path="/mock-test" element={<PrivatePage><MockTestLobby /></PrivatePage>} />
        <Route path="/mock-test/run/:moduleId" element={<PrivatePage><MockTestRunPage /></PrivatePage>} />
        <Route path="/mock-test/break" element={<PrivatePage><MockTestBreakPage /></PrivatePage>} />
        <Route path="/mock-test/results" element={<PrivatePage><MockTestResultsPage /></PrivatePage>} />
        <Route path="/mock-test/history" element={<PrivatePage><MockTestHistoryPage /></PrivatePage>} />
        <Route path="/gamepad" element={<Navigate to="/settings" replace />} />
        <Route path="/scores" element={<PrivatePage><ScoreView /></PrivatePage>} />
        <Route path="/history" element={<PrivatePage><ScoreHistory /></PrivatePage>} />
        <Route path="/history/:moduleId" element={<PrivatePage><ScoreHistory /></PrivatePage>} />
        <Route path="/account" element={<Navigate to="/settings" replace />} />
        <Route path="/subscription/success" element={<SubscriptionSuccess />} />
        <Route path="/subscription/cancel" element={<SubscriptionCancel />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
      <SiteDisclaimer />
      {/* Over every page, public practice pages included, for a signed-in
          account that hasn't accepted -- except the password-reset and
          email-verification pages, which must stay usable. */}
      {!location.pathname.startsWith('/auth') && <TermsAcceptanceGate />}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
